/**
 * Session 7 Phase 1 — Weekly reports (aggregated raw).
 * Table + CSV export (client-side).
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";

function eur(cents) {
  if (typeof cents !== "number") return "—";
  return `€ ${(cents / 100).toLocaleString("de-DE",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toCsv(items) {
  const header = ["tenant_slug", "week_start", "leads", "campaigns_active",
                       "budget_spent_cents", "gross_profit_cents_estimated"];
  const lines = [header.join(",")];
  items.forEach((r) => {
    lines.push(header.map((k) => JSON.stringify(r[k] ?? "")).join(","));
  });
  return lines.join("\n");
}

export default function OperatorWeeklyReports() {
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/operator/weekly-reports/summary?weeks=${weeks}`)
      .then((r) => setData(r.data));
  }, [weeks]);

  const exportCsv = () => {
    if (!data?.items) return;
    const blob = new Blob([toCsv(data.items)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `weekly-reports-${weeks}w.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="operator-weekly-reports">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">
          Wöchentliche Rohdaten
        </h1>
        <p className="text-sm text-muted-foreground">
          Aggregiert aus Kampagnen, Budget-Ledger und Leads. Kein
          ReportingAgent-Output. CSV-Export für Weiterverarbeitung.
        </p>
      </header>

      <div className="flex items-center gap-2">
        {[4, 8, 12].map((w) => (
          <Button
            key={w}
            size="sm"
            variant={weeks === w ? "default" : "outline"}
            onClick={() => setWeeks(w)}
            data-testid={`weekly-range-${w}`}
          >{w} Wochen</Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" onClick={exportCsv}
                disabled={!data?.items?.length}
                data-testid="weekly-export-csv">CSV exportieren</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rohdaten pro Tenant × Woche</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Woche</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Aktive Kampagnen</TableHead>
                <TableHead className="text-right">Ausgaben</TableHead>
                <TableHead className="text-right">Roh-DB estimated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!data || data.items?.length === 0) && (
                <TableRow data-testid="weekly-empty">
                  <TableCell colSpan={6} className="text-sm text-muted-foreground">
                    Keine Daten.
                  </TableCell>
                </TableRow>
              )}
              {data?.items?.map((r, i) => (
                <TableRow key={i}
                          data-testid={`weekly-row-${r.tenant_slug}-${r.week_start?.slice(0,10)}`}>
                  <TableCell><code>{r.tenant_slug}</code></TableCell>
                  <TableCell>{r.week_start?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">{r.leads}</TableCell>
                  <TableCell className="text-right">{r.campaigns_active}</TableCell>
                  <TableCell className="text-right">{eur(r.budget_spent_cents)}</TableCell>
                  <TableCell className="text-right">{eur(r.gross_profit_cents_estimated)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
